// src/pages/SubscriptionPage.js

import React, { useContext, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSubscriptionForm } from '../hooks/useSubscriptionForm';
import { AuthContext } from '../context/AuthContext';

// Ícones editoriais
import { 
    User, Users, Crown, Calendar, Sparkles, 
    CheckCircle2, CreditCard, ShieldCheck, ArrowRight 
} from 'lucide-react';

import Config from "../Config.json";
import ICON from "../assets/icons/favicon.ico";

const TITLE = Config.SITE_TITLE + " - Assinatura";
const DESC = "Jogue ilimitado e economize com seu grupo na Deli da Persy.";
const CANONICAL = Config.SITE_DOMAIN + "/assinatura";

// --- DADOS PARA CÁLCULO DE UI ---
const RAW_PRICES = {
    1: { "1": 88, "6": 480, "12": 864 },
    2: { "1": 158, "6": 858, "12": 1498 },
    4: { "1": 296, "6": 1496, "12": 2696 },
};

// --- SUBCOMPONENTES VISUAIS (Design System) ---

const StepHeader = ({ step, title, subtitle }) => (
    <div className="mb-8 mt-4">
        <div className="flex items-center gap-3 mb-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#dbcdac]/30 text-[#971253] font-serif font-bold text-sm">
                {step}
            </span>
            <h2 className="font-serif text-3xl text-[#971253]">{title}</h2>
        </div>
        {subtitle && <p className="text-stone-500 font-light ml-11">{subtitle}</p>}
    </div>
);

const PlanCard = ({ value, label, subtitle, icon: Icon, isSelected, onClick }) => (
    <div 
        onClick={() => onClick(value)}
        className={`
            relative p-6 rounded-2xl cursor-pointer transition-all duration-300 border-2 flex flex-col items-center text-center h-full
            ${isSelected 
                ? 'bg-[#971253]/5 border-[#971253] shadow-[0_10px_20px_-10px_rgba(151,18,83,0.3)] scale-[1.02]' 
                : 'bg-white border-[#dbcdac]/40 hover:border-[#528a89]/50 hover:shadow-md'
            }
        `}
    >
        {isSelected && (
            <div className="absolute top-3 right-3 text-[#971253]">
                <CheckCircle2 size={20} className="animate-in zoom-in duration-300" />
            </div>
        )}
        <div className={`p-4 rounded-full mb-4 transition-colors ${isSelected ? 'bg-[#971253] text-[#FFF5EE]' : 'bg-[#528a89]/10 text-[#528a89]'}`}>
            <Icon size={32} strokeWidth={1.5} />
        </div>
        <h3 className={`font-serif text-2xl mb-1 ${isSelected ? 'text-[#971253]' : 'text-stone-700'}`}>{label}</h3>
        <p className="text-sm text-stone-500 font-light">{subtitle}</p>
    </div>
);

const DurationCard = ({ months, title, badge, isSelected, onClick, currentPeople }) => {
    const basePrice1Mo = RAW_PRICES[currentPeople]["1"];
    const currentPrice = RAW_PRICES[currentPeople][months];
    const monthlyEquivalent = currentPrice / months;
    const totalWithoutDiscount = basePrice1Mo * months;
    const savings = totalWithoutDiscount - currentPrice;

    return (
        <div 
            onClick={() => onClick(months)}
            className={`
                relative p-6 rounded-2xl cursor-pointer transition-all duration-300 border-2 flex flex-col h-full
                ${isSelected 
                    ? 'bg-[#528a89]/5 border-[#528a89] shadow-[0_10px_20px_-10px_rgba(82,138,137,0.2)] scale-[1.02]' 
                    : 'bg-white border-[#dbcdac]/40 hover:border-[#528a89]/50 hover:shadow-md'
                }
            `}
        >
            {badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#dbcdac] text-[#971253] text-[10px] font-bold uppercase tracking-widest px-4 py-1 rounded-full shadow-sm whitespace-nowrap">
                    {badge}
                </div>
            )}
            
            <div className="flex justify-between items-start mb-4">
                <h3 className={`font-serif text-2xl ${isSelected ? 'text-[#528a89]' : 'text-stone-700'}`}>{title}</h3>
                {isSelected && <CheckCircle2 size={24} className="text-[#528a89] animate-in zoom-in duration-300" />}
            </div>

            <div className="mt-auto">
                <div className="flex items-baseline gap-1">
                    <span className="text-sm font-bold text-stone-500">R$</span>
                    <span className={`font-serif text-4xl font-bold ${isSelected ? 'text-[#528a89]' : 'text-stone-700'}`}>
                        {monthlyEquivalent.toFixed(0)}
                    </span>
                    <span className="text-sm text-stone-500">/mês</span>
                </div>
                
                {savings > 0 ? (
                    <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-[#971253]/10 text-[#971253] rounded-md text-xs font-bold uppercase tracking-wide">
                        <Sparkles size={12} /> Economia de R$ {savings}
                    </div>
                ) : (
                    <div className="mt-3 text-xs text-stone-400 uppercase tracking-wide px-1">
                        Plano Base
                    </div>
                )}
            </div>
        </div>
    );
};

// --- COMPONENTE PRINCIPAL ---

function SubscriptionPage() {
    const { user } = useContext(AuthContext);
    const location = useLocation();

    const initialData = useMemo(() => location.state?.prefillData || {}, [location.state]);
    
    // 👇 UPDATED: Destructure the submission states from the hook
    const {
        numPessoas, duracao, participants, price,
        handleParticipantChange, setNumPessoasDirectly, setDuracaoDirectly,
        createSubscription, isSubmitting 
    } = useSubscriptionForm(initialData); 

    const handleSubmit = (event) => {
        event.preventDefault();

        // 🛡️ Dupla validação de segurança via JavaScript antes de enviar para a API
        const invalidEmails = participants.some(p => !p.email.toLowerCase().endsWith('@gmail.com'));
        if (invalidEmails) {
            alert("Aviso: Todos os integrantes do clube precisam usar uma conta do Google (@gmail.com) válida.");
            return;
        }

        // Validação de dados vazios por segurança
        if (participants.some(p => !p.firstName || !p.lastName || !p.phone)) {
            alert("Por favor, preencha o nome, sobrenome e telefone de todos os participantes.");
            return;
        }

        // 👇 Triggers the new API payload call
        createSubscription();
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    
    if (user === undefined) { 
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FFF5EE]">
                <div className="w-12 h-12 border-4 border-[#dbcdac] border-t-[#971253] rounded-full animate-spin"></div>
            </div>
        );
    }
    
    if (user === null) return null; 

    // --- CLASSES DE ESTILIZAÇÃO DE INPUTS ---
    const defaultClasses = "bg-white border border-[#dbcdac]/60 shadow-sm text-stone-700 focus:border-[#528a89] focus:ring-2 focus:ring-[#528a89]/10";
    const readOnlyClasses = "bg-stone-100/70 border-stone-200 text-stone-500 shadow-none cursor-not-allowed pointer-events-none";
    const baseClasses = "w-full rounded-lg px-4 py-3 placeholder:text-stone-400 focus:outline-none transition-all";

    return (
        <div className="min-h-screen bg-[#FFF5EE] pt-24 pb-32 relative overflow-hidden font-sans text-stone-800">
            <title>{TITLE}</title>
            <link rel="icon" href={ICON} />
            <link rel="canonical" href={CANONICAL} />
            <meta name="description" content={DESC} />

            <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/aged-paper.png')]" />

            <div className="max-w-5xl mx-auto px-6 relative z-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                
                {/* HERO HEADER */}
                <div className="text-center mb-16">
                    <Crown className="mx-auto w-10 h-10 text-[#dbcdac] mb-4" />
                    <h1 className="font-serif text-5xl md:text-6xl text-[#971253] mb-4">Clube do Assinante</h1>
                    <p className="text-lg text-[#528a89] font-light max-w-2xl mx-auto">
                        Jogue à vontade. Sem taxas extras por mesa. <br className="hidden md:block"/>
                        Escolha seu plano e garanta seu lugar no acervo da Deli da Persy.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                    
                    {/* STEP 1: TAMANHO DO GRUPO */}
                    <div className="bg-white/60 backdrop-blur-sm p-8 md:p-12 rounded-[2rem] border border-[#dbcdac]/30 shadow-sm">
                        <StepHeader step="1" title="Tamanho do Grupo" subtitle="Quantos confrades farão parte dessa assinatura?" />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <PlanCard value={1} label="Aventureiro Solo" subtitle="1 Pessoa" icon={User} isSelected={numPessoas === 1} onClick={setNumPessoasDirectly} />
                            <PlanCard value={2} label="Dupla Dinâmica" subtitle="2 Pessoas" icon={Users} isSelected={numPessoas === 2} onClick={setNumPessoasDirectly} />
                            <PlanCard value={4} label="A Party Completa" subtitle="4 Pessoas" icon={Crown} isSelected={numPessoas === 4} onClick={setNumPessoasDirectly} />
                        </div>
                    </div>

                    {/* STEP 2: DURAÇÃO */}
                    <div className="bg-white/60 backdrop-blur-sm p-8 md:p-12 rounded-[2rem] border border-[#dbcdac]/30 shadow-sm">
                        <StepHeader step="2" title="Tempo de Assinatura" subtitle="Planos mais longos garantem descontos exclusivos no valor mensal." />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
                            <DurationCard months={1} title="Mensal" currentPeople={numPessoas} isSelected={duracao === 1} onClick={setDuracaoDirectly} />
                            <DurationCard months={6} title="Semestral" currentPeople={numPessoas} badge="Mais Popular" isSelected={duracao === 6} onClick={setDuracaoDirectly} />
                            <DurationCard months={12} title="Anual" currentPeople={numPessoas} badge="Melhor Valor" isSelected={duracao === 12} onClick={setDuracaoDirectly} />
                        </div>
                    </div>

                    {/* STEP 3: DADOS DOS PARTICIPANTES */}
                    <div className="bg-white/60 backdrop-blur-sm p-8 md:p-12 rounded-[2rem] border border-[#dbcdac]/30 shadow-sm">
                        <StepHeader step="3" title="Identificação" subtitle="Quem são os portadores deste passe? (Apenas contas @gmail.com)" />
                        
                        <div className="space-y-6">
                            {participants.map((participant, index) => (
                                <div key={index} className="bg-[#FFF5EE]/50 p-6 md:p-8 rounded-2xl border border-[#dbcdac]/40 relative">
                                    <div className="absolute -left-3 -top-3 w-8 h-8 rounded-full bg-[#528a89] text-white flex items-center justify-center font-bold text-sm shadow-md">
                                        {index + 1}
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-1">
                                            <input 
                                                type="text" name="firstName" placeholder="Nome" 
                                                value={participant.firstName} required 
                                                onChange={(e) => handleParticipantChange(index, e.target.name, e.target.value)}
                                                className={`${baseClasses} ${defaultClasses}`}
                                            />
                                        </div>
                                        <div className="md:col-span-1">
                                            <input 
                                                type="text" name="lastName" placeholder="Sobrenome" 
                                                value={participant.lastName} required
                                                onChange={(e) => handleParticipantChange(index, e.target.name, e.target.value)}
                                                className={`${baseClasses} ${defaultClasses}`}
                                            />
                                        </div>
                                        {/* E-MAIL: Validação HTML5 e pattern bloqueando domínios que não sejam gmail */}
                                        <div className="md:col-span-1 relative">
                                            <input 
                                                type="email" name="email" placeholder="E-mail (@gmail.com)" 
                                                value={participant.email} readOnly={index === 0} required
                                                pattern=".*@gmail\.com$"
                                                title="Apenas e-mails do Google (@gmail.com) são permitidos."
                                                onChange={(e) => handleParticipantChange(index, e.target.name, e.target.value)}
                                                className={`${baseClasses} ${index === 0 ? readOnlyClasses : defaultClasses}`}
                                            />
                                        </div>
                                        <div className="md:col-span-1">
                                            <input 
                                                type="tel" name="phone" placeholder="WhatsApp" maxLength={14}
                                                value={participant.phone} readOnly={index === 0} required
                                                onChange={(e) => handleParticipantChange(index, e.target.name, e.target.value)}
                                                className={`${baseClasses} ${index === 0 ? readOnlyClasses : defaultClasses}`}
                                            />
                                        </div>
                                    </div>

                                </div>
                            ))}
                        </div>
                    </div>

                    {/* STEP 4: CHECKOUT (RESUMO) */}
                    <div className="bg-gradient-to-br from-[#971253] to-[#7a0e42] p-6 sm:p-8 md:p-12 rounded-[2rem] shadow-2xl text-white relative overflow-hidden">
                        <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />
                        
                        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8 md:gap-10">
                            <div className="w-full md:w-1/2 space-y-2 text-center md:text-left flex flex-col items-center md:items-start">
                                <h3 className="font-serif text-2xl md:text-3xl text-[#dbcdac] mb-2">Resumo do Plano</h3>
                                <p className="text-white/80 font-light mb-4 text-sm md:text-base">
                                    Acesso liberado para {numPessoas} {numPessoas > 1 ? 'pessoas' : 'pessoa'} por {duracao} {duracao > 1 ? 'meses' : 'mês'}.
                                </p>
                                
                                <div className="flex flex-col sm:flex-row items-center sm:items-center justify-center md:justify-start gap-1 sm:gap-3 mt-2 mb-4">
                                    <span className="text-lg md:text-xl opacity-80">Pagamento Único:</span>
                                    <span className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight">{price}</span>
                                </div>
                                
                                <p className="text-xs md:text-sm text-white/60 flex items-center justify-center md:justify-start gap-1.5 mt-2">
                                    <ShieldCheck size={16} className="shrink-0"/> 
                                    <span>Pagamento seguro. Renovação não automática.</span>
                                </p>
                            </div>

                            <div className="w-full md:w-1/2 flex flex-col gap-4 mt-2 md:mt-0">
                                <button 
                                    type="submit" 
                                    // 👇 UPDATED: Desabilita enquanto salva
                                    disabled={isSubmitting}
                                    className="w-full py-4 md:py-5 px-2 bg-[#dbcdac] text-[#971253] rounded-xl font-bold tracking-wide md:tracking-wider text-[15px] sm:text-base md:text-lg hover:bg-white hover:-translate-y-1 transition-all shadow-lg flex items-center justify-center gap-2 md:gap-3 disabled:opacity-75 disabled:hover:translate-y-0"
                                >
                                    <CreditCard size={18} className="shrink-0 md:w-5 md:h-5" />
                                    {/* 👇 UPDATED: Mensagem dinâmica */}
                                    <span>{isSubmitting ? "Processando..." : "Continuar para Pagamento"}</span>
                                    {!isSubmitting && <ArrowRight size={18} className="shrink-0 md:w-5 md:h-5" />}
                                </button>
                            </div>
                        </div>
                    </div>

                </form>
            </div>
        </div>
    );
}

export default SubscriptionPage;